import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import {
  CreateProjectDto,
  CreateProjectDtoEstimatedDurationUnitEnum,
  CreateProjectDtoPriorityEnum,
  CreateProjectDtoStatusEnum,
} from 'src/Services/API/generated/api';
import { ProjectLabel } from 'src/Utils/projectLabelColors';

export const EMPTY_DESCRIPTION: SlateNode[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export interface ProjectFormValues {
  title: string;
  description: SlateNode[];
  status: CreateProjectDtoStatusEnum;
  estimatedCost?: number;
  estimatedDuration?: number;
  estimatedDurationUnit?: CreateProjectDtoEstimatedDurationUnitEnum;
  deadline?: Date | null;
  completionDate?: Date | null;
  label?: ProjectLabel | null;
  priority?: CreateProjectDtoPriorityEnum | null;
  memberIds: string[];
  participantIds: string[];
  attachments: FormAttachment[];
}

interface UseCreateProjectFormOptions {
  initialStatus?: CreateProjectDtoStatusEnum;
}

const isDescriptionEmpty = (description: SlateNode[]) => {
  if (!description || description.length === 0) return true;
  return JSON.stringify(description) === JSON.stringify(EMPTY_DESCRIPTION);
};

export const useCreateProjectForm = (options?: UseCreateProjectFormOptions) => {
  const { t } = useTranslation();

  const form = useForm<ProjectFormValues, (values: ProjectFormValues) => CreateProjectDto>({
    initialValues: {
      title: '',
      description: EMPTY_DESCRIPTION,
      status: options?.initialStatus ?? CreateProjectDtoStatusEnum.IdeaBox,
      estimatedCost: undefined,
      estimatedDuration: undefined,
      estimatedDurationUnit: CreateProjectDtoEstimatedDurationUnitEnum.Days,
      deadline: null,
      completionDate: null,
      label: null,
      priority: null,
      memberIds: [],
      participantIds: [],
      attachments: [],
    },
    validate: {
      title: (value: string) =>
        value.length < 2 ? t('common.errors.tooShort', 'Trop court') : null,
    },
    transformValues: (values: ProjectFormValues) => {
      const { attachments, deadline, completionDate, label, priority, description, ...rest } =
        values;
      return {
        ...rest,
        description: isDescriptionEmpty(description) ? undefined : JSON.stringify(description),
        deadline: deadline ?? undefined,
        completionDate: completionDate ?? undefined,
        label: label ?? undefined,
        priority: priority ?? undefined,
        attachmentIds: attachments.map(a => a.fileId).filter(Boolean) as string[],
      } as CreateProjectDto;
    },
  });

  return { form };
};
